

































































































































































































































import { chartBackgroundColor, filters, grouping } from '@/config/sales_report';
import MultiSelect from '@shared/components/forms/inputs/multi-select/multi-select.vue';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';
import 'vue2-datepicker/locale/nl';
import 'vue2-datepicker/locale/fr';
import 'vue2-datepicker/locale/es';
import { add, addYears, differenceInCalendarDays, isAfter } from 'date-fns';
import { ChartType, DisplayOptions } from '@shared/interfaces/chart-interfaces';
import { SelectOptionInterface } from '@shared/interfaces/inputs';
import ChartWrapper from '@shared/layouts/wrappers/chart-wrapper.vue';
import { SaleReportChartData, SalesReportData } from '@accounting/interfaces/sales-report';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import BCChart from '@shared/components/charts/bc-chart.vue';
import Table from '@shared/components/tables/table.vue';
import { ChartData, ChartOptions } from 'chart.js';
import SkeletonBox from '@shared/components/mocks/skeleton-box/skeleton-box.vue';
import TableExportsToCSV from '@shared/components/tables/abstract-table/table-exports-to-csv';
import { mixins } from 'vue-class-component';
import DownloadPdfUsingTemplateMixin from '@shared/components/tables/abstract-table/download-pdf-using-template-mixing';
import ModulesHelper from '@core/services/modules.helper';

interface SalesReportFilters {
  period: string;
  date_from?: Date;
  date_to?: Date;
  article_types?: string[];
  article_tag_ids?: number[];
  account_ids?: number[];
  analytic_account_ids?: number[];
  articles?: number[];
  order_status?: string;
  pos_box_id?: number;
  origin?: string;
}

@Component({
  name: 'bc-sales-report-chart',
  components: {
    'bc-chart-wrapper': ChartWrapper,
    'bc-multi-select': MultiSelect,
    'date-picker': DatePicker,
    'bc-chart': BCChart,
    'bpc-table': Table,
    'bpc-entity-select': EntitySelect,
    'bpc-skeleton-box': SkeletonBox,
  },
})
export default class SalesReportChart extends mixins(
  Vue,
  TableExportsToCSV,
  DownloadPdfUsingTemplateMixin,
) {
  declare $refs: TableExportsToCSV['$refs'] & {
    salesChart: InstanceType<typeof BCChart> & {
      renderChart(chartData: ChartData, options?: ChartOptions): void;
    };
  };

  // Props
  @Prop({ type: String, default: 'this_month' }) readonly defaultPeriod: string | null;
  @Prop({ type: String, default: 'article_and_price' }) readonly defaultGrouping: string | null;
  @Prop({
    type: Object,
    default: {
      showChartBar: false,
      showChartLine: false,
      showChartArea: false,
      showChartPie: true,
    },
  })
  readonly displayOptions: DisplayOptions | null;
  @Prop({ type: String }) readonly pageActionsPortalTarget: string;

  // Data
  showExportModal: boolean = false;
  showGeneratePDFModal: boolean = false;
  generating: boolean = false;
  defaultChartType: ChartType = ChartType.DoughnutChart;
  chartType: ChartType = ChartType.DoughnutChart;

  filters: SalesReportFilters = {
    period: '',
    date_from: null,
    date_to: null,
    article_types: [],
    article_tag_ids: [],
    pos_box_id: null,
    origin: null,
  };

  grouping: string = null;

  salesReport: SalesReportData = {
    sale_report_data: [],
    total_amounts: {
      total: 0,
      total_formatted: '0',
      total_taxes_price: 0,
      total_taxes_price_formatted: '0',
      total_without_taxes: 0,
      total_without_taxes_formatted: '0',
    },
  };

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      fullWidth: true,
      position: 'top',
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const label = data.labels[tooltipItem.index];
          const number = data.datasets[0].data[tooltipItem.index];

          const formattedNumber = this.$bpcHelper.FRmoneyFormat(number);
          return label + ': ' + formattedNumber + '€';
        },
      },
    },
  };

  loading: boolean = false;

  currentPage: number = 1;
  itemsPerPage: number = 15;

  chartData: ChartData = {
    labels: ['General account 1', 'General account 2', 'General account 3', 'General account 3'],
    datasets: [
      {
        label: 'My First Dataset',
        data: [300, 50, 100, 1000],
        backgroundColor: this.chartBackgroundColor,
      },
    ],
  };

  sortBy: string = 'total';
  sortDesc: boolean = true;

  articleTagFieldConfig = {
    selectEntity: 'article-tag',
    selectLabel: 'name',
    selectValue: 'id',
  };

  // Computed
  get showDateRange(): boolean {
    return this.filters.period === 'custom';
  }

  get isTaxManagementModuleActivated(): boolean {
    return this.$modulesService.hasModuleActivated(ModulesHelper.TAX_MANAGEMENT_MODULE_NAME);
  }
  get isPosModuleActivated(): boolean {
    return this.$modulesService.hasModuleActivated(ModulesHelper.POS_MODULE_NAME);
  }

  /**
   * Computes the sliced sales report data.
   * If there are 20 or fewer records, returns the data without modification.
   * If there are more than 20 records, groups the top 20 records and aggregates the rest under the label 'Others'.
   * @returns {SaleReportChartData} The sliced sales report data.
   */
  get slicedSalesReport(): SaleReportChartData {
    // Check if there are less than or equal to 20 elements

    if (this.salesReport?.sale_report_data?.length <= 20) {
      // If there are 20 or fewer elements, return the data without modification
      return {
        labels: this.salesReport.sale_report_data.map(item => item.label),
        totals: this.salesReport.sale_report_data.map(item => item.total),
      };
    }

    // Sort the data by the 'totals' field in descending order
    const sortedSalesReport = this.salesReport.sale_report_data.sort((a, b) => b.total - a.total);
    // Take the top 20 records
    const top20 = sortedSalesReport.slice(0, 20);
    // Calculate the sum of the total for the remaining records
    const restTotal = sortedSalesReport.slice(20).reduce((total, item) => total + item.total, 0);
    // Group the remaining records under the label 'Others'
    const others = {
      label: this.$t('reports.sales_report.others') as string,
      total: restTotal,
    };
    // Return the data combining the top 20 and the 'Others' group
    return {
      labels: [...top20.map(item => item.label), others.label],
      totals: [...top20.map(item => item.total), others.total],
    };
  }

  get total_formatted(): string | null {
    console.log(this.salesReport);
    if (this.salesReport.total_amounts.total_formatted) {
      return this.salesReport.total_amounts.total_formatted + ' €';
    }
    return '0,00 €';
  }

  get total_without_taxes_formatted(): string | null {
    if (this.salesReport.total_amounts.total_without_taxes_formatted) {
      return this.salesReport.total_amounts.total_without_taxes_formatted + ' €';
    }
    return '0,00 €';
  }

  get total_taxes_price_formatted(): string | null {
    if (this.salesReport.total_amounts.total_taxes_price_formatted) {
      return this.salesReport.total_amounts.total_taxes_price_formatted + ' €';
    }
    return '0,00 €';
  }

  get noResults(): boolean {
    return this.salesReport.sale_report_data.length === 0;
  }

  get tableFields() {
    let labelToShow = '';
    if (this.grouping) {
      labelToShow = this.$t(`reports.sales_report.grouping.option.${this.grouping}`) as string;
    } else if (this.filters.period === 'custom') {
      const dateFrom = this.filters.date_from ? this.formatDate(this.filters.date_from) : '';
      const dateTo = this.filters.date_to ? this.formatDate(this.filters.date_to) : '';
      labelToShow =
        this.$t('reports.date_from.label') +
        ' ' +
        dateFrom +
        this.$t('reports.date_to.label') +
        ' ' +
        dateTo;
    } else {
      labelToShow = this.$t(`reports.sales_report.period.option.${this.filters.period}`) as string;
    }

    const defaultTableFields = [
      { key: 'label', label: labelToShow, sortable: true },
      {
        key: 'total',
        label: this.$t('reports.report.total'),
        sortable: true,
        tdClass: 'text-right',
        thClass: 'text-right',
      },
    ];

    const accountGroupingTableFields = [
      { key: 'label', label: labelToShow, sortable: true },
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_without_taxes',
            label: this.$t('reports.report.total_without_taxes'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_taxes_price',
            label: this.$t('reports.report.total_taxes_price'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      {
        key: 'total',
        label: this.$t('reports.report.total'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'percentage_of_amount_over_total_amount',
        label: this.$t('reports.report.percentage_of_amount_over_total_amount'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
    ];

    const articleGroupingTableFields = [
      { key: 'code', label: this.$t('reports.report.code'), sortable: true },
      { key: 'label', label: labelToShow, sortable: true },
      {
        key: 'total_quantity',
        label: this.$t('reports.report.total_quantity'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'percentage_of_quantity_over_total',
        label: this.$t('reports.report.percentage_of_quantity_over_total'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_without_taxes',
            label: this.$t('reports.report.total_without_taxes'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_taxes_price',
            label: this.$t('reports.report.total_taxes_price'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      {
        key: 'total',
        label: this.$t('reports.report.total'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'percentage_of_amount_over_total_amount',
        label: this.$t('reports.report.percentage_of_amount_over_total_amount'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
    ];

    const articleAndPriceGroupingTableFields = [
      { key: 'code', label: this.$t('reports.report.code'), sortable: true },
      { key: 'label', label: labelToShow, sortable: true },
      {
        key: 'unit_price',
        label: this.$t('reports.report.unit_price'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      this.isTaxManagementModuleActivated
        ? {
            key: 'tax_rate',
            label: this.$t('reports.report.tax_rate'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      {
        key: 'total_quantity',
        label: this.$t('reports.report.total_quantity'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      this.isTaxManagementModuleActivated
        ? {
            key: 'percentage_of_quantity_over_total',
            label: this.$t('reports.report.percentage_of_quantity_over_total'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_without_taxes',
            label: this.$t('reports.report.total_without_taxes'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      this.isTaxManagementModuleActivated
        ? {
            key: 'total_taxes_price',
            label: this.$t('reports.report.total_taxes_price'),
            sortable: true,
            thClass: 'text-right',
            tdClass: 'text-right',
          }
        : null,
      {
        key: 'total',
        label: this.$t('reports.report.total'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'percentage_of_amount_over_total_amount',
        label: this.$t('reports.report.percentage_of_amount_over_total_amount'),
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
    ];

    if (this.grouping === 'article') {
      return articleGroupingTableFields;
    } else if (this.grouping === 'general_account') {
      return accountGroupingTableFields;
    } else if (this.grouping === 'analytic_account') {
      return accountGroupingTableFields;
    } else if (this.grouping === 'article_and_price') {
      return articleAndPriceGroupingTableFields;
    }

    return defaultTableFields;
  }

  // Filters
  get periodFilterOptions(): SelectOptionInterface[] {
    return filters?.period?.options || [];
  }
  get periodFilterPlaceholder(): string {
    return this.$t(`${filters?.period?.placeholder}`).toString();
  }

  get articleTagFilterPlaceholder(): string {
    return this.$t(`reports.sales_report.article_tag`).toString();
  }

  get posBoxFilterPlaceholder(): string {
    return this.$t(`reports.sales_report.pos_box`).toString();
  }

  get originFilterPlaceholder(): string {
    return this.$t(`reports.sales_report.origin`).toString();
  }
  get originFilterOptions(): SelectOptionInterface[] {
    return filters?.origin?.options || [];
  }

  // Grouping
  get groupingOptions(): SelectOptionInterface[] {
    return grouping?.options;
  }

  get groupingPlaceholder(): string {
    return this.$t(`${grouping?.placeholder}`).toString();
  }
  get chartBackgroundColor(): any {
    return chartBackgroundColor;
  }

  get noLabel() {
    return {
      labelAttributes: {
        for: 'test',
      },
    };
  }

  get tableItems() {
    return this.salesReport.sale_report_data.map(saleReport => {
      const defaultTableItem = {
        label: saleReport.label,
        total: saleReport.total,
      };

      const accountGroupingTableItem = {
        ...defaultTableItem,
        total_without_taxes: saleReport.total_without_taxes,
        total_taxes_price: saleReport.total_taxes_price,
        percentage_of_amount_over_total_amount:
          saleReport.percentage_of_amount_over_total_amount_formatted + ' %',
      };
      const articleGroupingTableItem = {
        ...defaultTableItem,
        code: saleReport.code,
        total_quantity: saleReport.total_quantity,
        percentage_of_quantity_over_total:
          saleReport.percentage_of_quantity_over_total_formatted + ' %',
        total_without_taxes: saleReport.total_without_taxes,
        total_taxes_price: saleReport.total_taxes_price,
        percentage_of_amount_over_total_amount:
          saleReport.percentage_of_amount_over_total_amount_formatted + ' %',
      };
      const articleAndPriceGroupingTableItem = {
        ...defaultTableItem,
        code: saleReport.code,
        unit_price: saleReport.unit_price,
        tax_rate: saleReport.tax_rate_formatted,
        total_quantity: saleReport.total_quantity,
        percentage_of_quantity_over_total:
          saleReport.percentage_of_quantity_over_total_formatted + ' %',
        total_without_taxes: saleReport.total_without_taxes,
        total_taxes_price: saleReport.total_taxes_price,
        percentage_of_amount_over_total_amount:
          saleReport.percentage_of_amount_over_total_amount_formatted + ' %',
      };

      if (this.grouping === 'general_account') {
        return accountGroupingTableItem;
      } else if (this.grouping === 'analytic_account') {
        return accountGroupingTableItem;
      } else if (this.grouping === 'article') {
        return articleGroupingTableItem;
      } else if (this.grouping === 'article_and_price') {
        return articleAndPriceGroupingTableItem;
      }

      return defaultTableItem;
    });
  }
  get totalRows(): number {
    return this.salesReport.sale_report_data.length;
  }
  // Methods

  disableDateTo(date: Date): boolean {
    if (!this.filters.date_from) {
      return false;
    }
    const startDate = new Date(this.filters.date_from);
    const oneYearFromStartDate = addYears(startDate, 1);
    return isAfter(date, oneYearFromStartDate);
  }

  onDateFromChange(): void {
    if (this.filters.date_to) {
      const startDate = new Date(this.filters.date_from);
      const endDate = new Date(this.filters.date_to);
      const dayDiff = differenceInCalendarDays(endDate, startDate);
      if (dayDiff > 365) {
        const newEndDate = add(startDate, { days: 365 });
        this.filters.date_to = newEndDate;
      }
    }
  }

  onChartTypeChange(chartType: ChartType) {
    this.chartType = chartType;
    this.renderChart();
  }

  renderChart(): void {
    this.$nextTick().then(() => {
      this.chartData = {
        labels: this.slicedSalesReport.labels,
        datasets: [
          {
            label: this.$t('global.sales_report.totals') as string,
            data: this.slicedSalesReport.totals,
            backgroundColor: this.chartBackgroundColor,
          },
        ],
      };

      this.$refs.salesChart.renderChart(this.chartData, this.chartOptions);
    });
  }

  async getSalesReport() {
    this.loading = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('sales-report');
    const filters = this.filters;
    const grouping = this.grouping;
    const date_from = this.$bpcHelper.getDateDbStrV2(this.filters.date_from);
    const date_to = this.$bpcHelper.getDateDbStrV2(this.filters.date_to);
    this.$bpcApi
      .get(url, {
        params: {
          ...filters,
          date_from: date_from,
          date_to: date_to,
          grouping: grouping,
        },
      })
      .subscribe(res => {
        this.loading = false;
        this.salesReport = res.data;
        this.sortBy = 'total';
        this.sortDesc = true;
        this.renderChart();
      });
  }

  onClickGeneratePDF() {
    this.generating = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('sales-report');
    const params = this.getQueryParams();
    this.$bpcApi.postDownload(`${url}/generate-pdf/`, '', { ...params }).subscribe(res => {
      this.generating = false;
    });
  }

  onClickExport() {
    this.showExportModal = true;
  }

  getQueryParams() {
    const filters = this.filters;
    const grouping = this.grouping;
    const orderBy = this.sortBy;
    const orderDirection = this.sortDesc ? 'desc' : 'asc';
    const date_from = this.$bpcHelper.getDateDbStrV2(this.filters.date_from);
    const date_to = this.$bpcHelper.getDateDbStrV2(this.filters.date_to);

    return {
      ...filters,
      date_from: date_from,
      date_to: date_to,
      grouping: grouping,
      order_by: orderBy,
      order_direction: orderDirection,
    };
  }

  formatDate(value): string {
    return this.$bpcHelper.formatDateV2(this.$bpcHelper.getDateDbStrV2(value));
  }

  moneyFormat(value) {
    return this.$bpcHelper.FRmoneyFormat(value);
  }

  // LIFECYCLE
  // created(){}
  mounted() {
    this.filters.period = this.defaultPeriod;
    this.grouping = this.defaultGrouping;
  }
  // updated(){}
  // destroyed(){}

  // Watchers
  @Watch('filters.period')
  onFilterPeriodChange(newVal: string, oldVal: string) {
    if (newVal === 'custom') {
      this.filters.date_from = null;
      this.filters.date_to = null;
    }

    if (newVal !== oldVal && newVal != 'custom') {
      this.getSalesReport();
    }
  }

  @Watch('grouping')
  onFilterDurationChange(newVal: string, oldVal: string) {
    if (newVal !== oldVal) {
      this.getSalesReport();
    }
  }
  @Watch('filters.date_from')
  onFilterDateFromChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      if (this.filters.date_to) {
        this.getSalesReport();
      }
    }
  }
  @Watch('filters.date_to')
  onFilterDateToChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      if (this.filters.date_from) {
        this.getSalesReport();
      }
    }
  }
  @Watch('filters.article_tag_ids')
  onFilterArticleTagChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      this.getSalesReport();
    }
  }
  @Watch('filters.pos_box_id')
  onFilterPosBoxIdChange(newVal: string, oldVal: string) {
    if (newVal !== oldVal) {
      this.getSalesReport();
    }
  }
  @Watch('filters.origin')
  onFilterOriginChange(newVal: string, oldVal: string) {
    if (newVal !== oldVal) {
      this.getSalesReport();
    }
  }
}
